import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { Reliability, ReliabilityWindow, RowsTrend } from "@shared/types/reliability";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { usePermissions } from "@/hooks/use-permissions";
import { apiRequest } from "@/lib/api";

/**
 * Level B reliability (#828): `GET /api/v2/instances/current/reliability?window=7d|30d`, read by
 * Connections, Schedules, the Overview and Your data, and `GET /api/v2/dataflows/:id/rows-trend`,
 * read by the run details page. Both are run history (`runs.read`, every instance role). The key
 * carries the instance id, so one instance's numbers never show under another's header. A minute's
 * stale time: freshness moves with the clock and the runs, not with each render.
 */
export const RELIABILITY_PATH = "/api/v2/instances/current/reliability";

/** The first element of every rows-trend query key (prefix-matched to invalidate them all). */
export const ROWS_TREND_KEY = "/api/v2/dataflows/rows-trend";

/**
 * A run finished (or was cancelled): its load records changed what Last loaded, freshness and the
 * rows trend read. Called from the live feed's terminal frame, from the run page's flip to
 * finished (a run read by poll with no socket) and from the cancel and retry mutations, so each
 * trigger covers a path the others can miss. A repeat call only restarts the refetch.
 */
export function invalidateRunHistory(
  queryClient: QueryClient,
  refetchType: "active" | "none" = "active",
): void {
  void queryClient.invalidateQueries({ queryKey: [RELIABILITY_PATH], refetchType });
  void queryClient.invalidateQueries({ queryKey: [ROWS_TREND_KEY], refetchType });
}

export function reliabilityQueryKey(
  instanceId: string | null,
  window: ReliabilityWindow,
): [string, string | null, ReliabilityWindow] {
  return [RELIABILITY_PATH, instanceId, window];
}

export function useReliability(window: ReliabilityWindow = "7d"): UseQueryResult<Reliability> {
  const { selectedInstance } = useTenantInstance();
  const { can } = usePermissions();
  const instanceId = selectedInstance?.id ?? null;
  return useQuery<Reliability>({
    queryKey: reliabilityQueryKey(instanceId, window),
    enabled: instanceId !== null && can("runs.read"),
    queryFn: ({ signal }) =>
      apiRequest<Reliability>(`${RELIABILITY_PATH}?window=${window}`, { signal }),
    staleTime: 60_000,
  });
}

export function rowsTrendPath(dataflowId: string, limit = 30): string {
  return `/api/v2/dataflows/${encodeURIComponent(dataflowId)}/rows-trend?limit=${limit}`;
}

/**
 * `readFresh` (a finished run's page): the trend is read again on every mount, so a card opened
 * after the run finished never shows an answer cached before its load record was written.
 */
export function useRowsTrend(
  dataflowId: string | null,
  limit = 30,
  readFresh = false,
): UseQueryResult<RowsTrend> {
  const { selectedInstance } = useTenantInstance();
  const { can } = usePermissions();
  const instanceId = selectedInstance?.id ?? null;
  return useQuery<RowsTrend>({
    queryKey: [ROWS_TREND_KEY, instanceId, dataflowId, limit],
    enabled: instanceId !== null && dataflowId !== null && can("runs.read"),
    queryFn: ({ signal }) =>
      apiRequest<RowsTrend>(rowsTrendPath(dataflowId as string, limit), { signal }),
    staleTime: 60_000,
    refetchOnMount: readFresh ? "always" : true,
  });
}
